// Wrapper around the Twilio chat client.  This code handles setting up
// twilio channels for communication to sms.

// Requires:
//  jquery,
//  Twilio Conversation Client
import { Client } from "@twilio/conversations";
import _ from "lodash";

const ChatToSms = function (baseSvcUrl) {
    const self = this;
    const _debug = false;

    self._twilio = {
        identity: generateUniqueId(),
        activeConversation: {},
        conversationClient: {}
    };

    self._baseSvcUrl = baseSvcUrl;
    const _chatMgrSvc = new ChatMgrSvc.Service(baseSvcUrl);

    // Generates a unique id representing this chat session
    function generateUniqueId() {
        return new Date().getTime().toString(16) + Math.floor(1e7 * Math.random()).toString(16);
    }

    // Step 1
    function getToken() {
        if (_debug) {
            console.log("Getting token...");
        }

        const payload = {
            Identity: self._twilio.identity
        };

        // Wrapping in my own promise because VtWebServices
        // uses its own promise which varies slightly to the
        // es2016 spec, ie (uses .done, .fail, .always)
        return new Promise(function (resolve, reject) {
            _chatMgrSvc
                .getChatToken(payload)
                .then(function (response) {
                    if (_debug) {
                        console.log(response.Token);
                    }
                    resolve(response.Token);
                })
                .fail(function (err) {
                    reject({ message: "Could not retrieve chat token from services.", error: err });
                });
        });
    }

    // Step 2
    function initChat(token) {
        if (_debug) {
            console.log("Initializing chat...");
        }

        const client = new Client(token);
        if (_debug) {
            console.log(client);
        }

        return new Promise(function (resolve, reject) {
            client
                .getSubscribedConversations()
                .then(function () {
                    resolve(client, token);
                })
                .catch(function (err) {
                    reject({ message: "Could not init conversation client.", error: err });
                });
        });

        //client.on('stateChanged', (state) => {
        //    if (state === 'initialized') {

        //    }
        //});
    }

    // Step 3
    function initConversation(conversationClient, token) {
        return new Promise(function (resolve, reject) {
            let uniqueString = token.substring(0, 255);

            conversationClient
                .getConversationByUniqueName(uniqueString)
                .then(function (conversation) {
                    resolve(conversation);
                    // At this point, we need to update conversation with user name, phone, etc
                })
                .catch(function () {
                    //If it doesn't exist, lets create it.
                    if (_debug) {
                        console.log("Creating Channel...");
                    }
                    conversationClient
                        .createConversation({
                            uniqueName: uniqueString,
                            friendlyName: "Chat with an Agent"
                        })
                        .then(function (conversation) {
                            if (_debug) {
                                console.log("Created Conversation: " + conversation.sid);
                            }
                            if (_debug) {
                                console.log(conversation);
                            }
                            resolve(conversation);
                        })
                        .catch(function (err) {
                            reject({ message: "Could not create chat client conversation.", error: err });
                        });
                });
        });
    }

    // Step 4 - public - this must be called by consuming code to setup
    // channel.  Emits message-added event with the message to be handled
    // by caller.
    self.joinConversation = function (conversation) {
        return new Promise(function (resolve, reject) {
            conversation
                .join()
                .then(function (conversation) {
                    if (_debug) {
                        console.log("joined conversation...");
                    }
                    resolve(conversation);
                })
                .catch(function (err) {
                    reject({ message: "Could not join chat client conversation.", error: err });
                });
        });
    };

    self.updateConversation = function (channelInfo) {
        const payload = _.assign({}, { ConversationSid: self._twilio.activeConversation.sid }, channelInfo);

        return new Promise(function (resolve, reject) {
            _chatMgrSvc
                .updateConversation(payload)
                .then(function () {
                    resolve(self._twilio.activeConversation);
                })
                .fail(function (err) {
                    reject({ message: "Could not update conversation.", error: err });
                });
        });
    };

    // PUBLIC
    self.sendMessage = function (message) {
        return new Promise(function (resolve, reject) {
            const payload = {
                Body: message,
                ConversationSid: self._twilio.activeConversation.sid
            };

            _chatMgrSvc
                .sendSms(payload)
                .then(function (response) {
                    if (_debug) {
                        console.log(response);
                    }
                    resolve(response.MessageSid);
                })
                .fail(function (err) {
                    reject({ message: "Unable to send chat message to services.", error: err });
                });
        });
    };

    // PUBLIC
    self.setupChat = function () {
        // Get a token and setup twilio chanel
        if (_debug) {
            console.log("Initializing chat client...");
        }
        let _token = "";

        return new Promise(function (resolve, reject) {
            getToken()
                .then(function (token) {
                    _token = token;
                    return initChat(_token);
                })
                .then(function (conversationClient) {
                    self._twilio.conversationClient = conversationClient;
                    return initConversation(conversationClient, _token);
                })
                .then(function (conversation) {
                    self._twilio.activeConversation = conversation;
                    resolve(self._twilio);
                })
                .catch(function (err) {
                    console.log(err);
                    reject(err);
                });
        });
    };

    self.endChat = function () {
        // Delete the channel if it exists.
        if (!_.isEmpty(self._twilio.activeConversation)) {
            self._twilio.activeConversation.delete();
        }
    };

    self.emailTranscript = function (sendToEmail) {
        return new Promise(function (resolve, reject) {
            const payload = {
                ConversationSid: self._twilio.activeConversation.sid,
                Email: sendToEmail
            };

            _chatMgrSvc
                .emailTranscript(payload)
                .then(function (response) {
                    if (_debug) {
                        console.log(response);
                    }
                    resolve(response.Code);
                })
                .fail(function (err) {
                    reject({ message: "Unable to email chat transcript.", error: err });
                });
        });
    };
};

export default ChatToSms;
